import { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useParams } from 'react-router-dom';
import AdminLayout from '@/components/layout/AdminLayout';
import { Button } from '@/components/ui/button';
import { Plus, Search, Filter, TrendingUp } from 'lucide-react';
import SuspendProviderModal from '@/components/modals/SuspendProviderModal';
import RestrictJobEligibilityModal from '@/components/modals/RestrictJobEligibilityModal';
import apiService from '@/services/api';

interface Provider {
  id: string;
  name: string;
  email: string;
  phone: string;
  avatar: string;
  services: string;
  serviceType: string;
  coverage: string;
  compliance: string;
  complianceStatus: 'Valid' | 'Expiring' | 'Expired';
  jobs: number;
  completion: number;
  completionVar: string;
  payout: string;
  payoutStatus: string;
  status: 'ACTIVE' | 'SUSPENDED' | 'HELD' | 'RESTRICTED';
}

const AdminProvidersPage = () => {
  const navigate = useNavigate();
  const { providerId: editProviderId } = useParams<{ providerId: string }>();
  const [currentPage, setCurrentPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [showSuspendModal, setShowSuspendModal] = useState(false);
  const [showRestrictModal, setShowRestrictModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedProvider, setSelectedProvider] = useState<Provider | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '', phone: '', status: 'active' });
  const [providers, setProviders] = useState<Provider[]>([]);
  const [fetchingProviders, setFetchingProviders] = useState(true);
  const itemsPerPage = 25;

  useEffect(() => {
    const fetchProviders = async () => {
      try {
        setFetchingProviders(true);
        const response = await apiService.request('/admin/providers', 'GET');
        if (response.success && Array.isArray(response.data)) {
          const mapped: Provider[] = response.data.map((p: any) => {
            const name = p.name || p.company_name || p.business_name || p.full_name || `${p.first_name || ''} ${p.last_name || ''}`.trim() || 'Unknown';
            const initials = name.split(' ').map((w: string) => w[0]).join('').toUpperCase().slice(0, 2);
            const totalJobs = p.total_jobs || p.jobs_count || p.completed_jobs || 0;
            const completionRate = p.completion_rate || (totalJobs > 0 ? 95 : 0);
            return {
              id: String(p.id || p.provider_id),
              name,
              email: p.email || '',
              phone: p.phone || '',
              avatar: initials,
              services: p.service_category || p.primary_service || '—',
              serviceType: (p.service_types || p.services || '—').toString().toUpperCase(),
              coverage: p.coverage_area || p.postcode || p.city || 'UK',
              compliance: p.compliance_status === 'valid' ? 'Valid' : p.compliance_status === 'expiring' ? 'Expiring' : p.compliance_status === 'expired' ? 'Expired' : 'Valid',
              complianceStatus: p.compliance_status === 'valid' ? 'Valid' : p.compliance_status === 'expiring' ? 'Expiring' : 'Expired',
              jobs: totalJobs,
              completion: completionRate,
              completionVar: `VAR:${Math.abs(100 - completionRate).toFixed(1)}%`,
              payout: p.payout_status?.toUpperCase() || 'CLEAR',
              payoutStatus: p.payout_status?.toUpperCase() || 'CLEAR',
              status: p.status === 'suspended' ? 'SUSPENDED' : p.status === 'restricted' ? 'RESTRICTED' : p.status === 'held' ? 'HELD' : 'ACTIVE',
            };
          });
          setProviders(mapped);
          if (editProviderId) {
            const match = mapped.find((p) => p.id === editProviderId);
            if (match) {
              setSelectedProvider(match);
              setFormData({
                name: match.name,
                email: match.email,
                phone: match.phone,
                status: match.status === 'SUSPENDED' ? 'suspended' : 'active',
              });
              setShowEditModal(true);
            }
          }
        }
      } catch (err) {
        console.error('Failed to load providers:', err);
      } finally {
        setFetchingProviders(false);
      }
    };
    fetchProviders();
  }, [editProviderId]);

  // ============================================================
  // PROVIDER HANDLERS
  // ============================================================

  const handleSuspendProvider = async (suspensionReason: string) => {
    if (!selectedProvider) return;

    setLoading(true);
    try {
      const response = await apiService.request(`/admin/providers/${selectedProvider.id}/suspend`, 'POST', {
        providerId: selectedProvider.id,
        suspensionReason: suspensionReason,
        suspendedBy: localStorage.getItem('adminId') || 'admin',
        suspendedAt: new Date().toISOString(),
        suspensionDuration: '30 days',
        canAppeal: true
      });

      if (response.success) {
        alert('✓ Provider suspended successfully');
        setShowSuspendModal(false);
        setSelectedProvider(null);
        window.location.reload();
      } else {
        alert('Error: ' + (response.message || 'Failed to suspend provider'));
      }
    } catch (error: any) {
      console.error('Error suspending provider:', error);
      alert('Failed to suspend provider: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const handleRestrictJobEligibility = async (restrictionType: string, reason: string) => {
    if (!selectedProvider) return;

    setLoading(true);
    try {
      const response = await apiService.request(`/admin/providers/${selectedProvider.id}/restrict`, 'POST', {
        providerId: selectedProvider.id,
        restrictionType: restrictionType,
        reason: reason,
        restrictedBy: localStorage.getItem('adminId') || 'admin',
        restrictedAt: new Date().toISOString(),
        canAppeal: true
      });

      if (response.success) {
        alert('✓ Provider job eligibility restricted');
        setShowRestrictModal(false);
        setSelectedProvider(null);
        window.location.reload();
      } else {
        alert('Error: ' + (response.message || 'Failed to restrict provider'));
      }
    } catch (error: any) {
      console.error('Error restricting provider:', error);
      alert('Failed to restrict provider: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const handleAddProvider = async () => {
    if (!formData.name || !formData.email) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.request('/admin/providers', 'POST', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        status: formData.status,
      });

      if (response.success) {
        alert('✓ Provider added successfully');
        setShowAddModal(false);
        setFormData({ name: '', email: '', phone: '', status: 'active' });
        window.location.reload();
      } else {
        alert('Error: ' + (response.message || 'Failed to add provider'));
      }
    } catch (error: any) {
      console.error('Error adding provider:', error);
      alert('Failed to add provider: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const handleUpdateProvider = async () => {
    if (!selectedProvider || !formData.name || !formData.email) {
      alert('Please fill in all required fields');
      return;
    }

    setLoading(true);
    try {
      const response = await apiService.request(`/admin/providers/${selectedProvider.id}`, 'PUT', {
        name: formData.name,
        email: formData.email,
        phone: formData.phone,
        status: formData.status,
      });

      if (response.success) {
        alert('✓ Provider updated successfully');
        setShowEditModal(false);
        setFormData({ name: '', email: '', phone: '', status: 'active' });
        setSelectedProvider(null);
        window.location.reload();
      } else {
        alert('Error: ' + (response.message || 'Failed to update provider'));
      }
    } catch (error: any) {
      console.error('Error updating provider:', error);
      alert('Failed to update provider: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteProvider = async () => {
    if (!selectedProvider) return;

    setLoading(true);
    try {
      const response = await apiService.request(`/admin/providers/${selectedProvider.id}`, 'DELETE');

      if (response.success) {
        alert('✓ Provider deleted successfully');
        setShowDeleteModal(false);
        setSelectedProvider(null);
        window.location.reload();
      } else {
        alert('Error: ' + (response.message || 'Failed to delete provider'));
      }
    } catch (error: any) {
      console.error('Error deleting provider:', error);
      alert('Failed to delete provider: ' + (error.message || error));
    } finally {
      setLoading(false);
    }
  };


  const getAvatarColor = (index: number) => {
    const colors = [
      'bg-gradient-to-br from-orange-500 to-orange-600',
      'bg-gradient-to-br from-amber-500 to-amber-600',
      'bg-gradient-to-br from-slate-700 to-slate-900',
      'bg-gradient-to-br from-teal-500 to-teal-600'
    ];
    return colors[index % colors.length];
  };

  const getComplianceColor = (status: string) => {
    switch (status) {
      case 'Valid':
        return 'bg-green-100 text-green-700 border border-green-300';
      case 'Expiring':
        return 'bg-orange-100 text-orange-700 border border-orange-300';
      case 'Expired':
        return 'bg-red-100 text-red-700 border border-red-300';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  const getPayoutColor = (status: string) => {
    switch (status) {
      case 'CLEAR':
        return 'text-green-600 bg-green-50 border border-green-200';
      case 'HELD':
        return 'text-orange-600 bg-orange-50 border border-orange-200';
      case 'NO_BAL':
        return 'text-gray-600 bg-gray-50 border border-gray-200';
      default:
        return 'text-gray-600 bg-gray-50';
    }
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-teal-100 text-teal-700 border border-teal-300';
      case 'SUSPENDED':
        return 'bg-gray-100 text-gray-700 border border-gray-300';
      case 'RESTRICTED':
        return 'bg-orange-100 text-orange-700 border border-orange-300';
      case 'HELD':
        return 'bg-yellow-100 text-yellow-700 border border-yellow-300';
      default:
        return 'bg-gray-100 text-gray-700';
    }
  };

  // const totalProviders removed - now uses state from API
  const startIdx = (currentPage - 1) * itemsPerPage;
  const displayedProviders = providers.slice(startIdx, startIdx + itemsPerPage);

  const handleOnSuspendConfirm = (reason: string) => {
    handleSuspendProvider(reason);
  };

  const handleOnRestrictConfirm = (type: string, reason: string) => {
    handleRestrictJobEligibility(type, reason);
  };

  // Handler: Clear filters
  const handleClearFilters = async () => {
    setSearchQuery('');
    setSelectedStatus('All');
    setSelectedSort('newest');
    // Reset any other filter states if they exist
    window.location.reload();
  };

  // Handler: Onboard verification process
  const handleOnboardVerification = async () => {
    if (!selectedProvider) {
      alert('Please select a provider');
      return;
    }

    try {
      const response = await apiService.request(`/admin/providers/${selectedProvider.id}/onboard-verify`, 'POST', {
        verifiedBy: localStorage.getItem('adminId'),
        verifiedAt: new Date().toISOString()
      });
      
      if (response.success) {
        alert('✓ Provider onboarding verification completed');
        setSelectedProvider(null);
        window.location.reload();
      } else {
        alert('Error: ' + response.message);
      }
    } catch (error: any) {
      console.error('Onboard verification error:', error);
      alert('Failed to verify onboarding: ' + error.message);
    }
  };

  return (
    <>
      <Helmet>
        <title>Providers Directory - Virewire Admin</title>
      </Helmet>

      <AdminLayout>
        <div className="bg-gray-50 min-h-screen p-8">
          {/* Header */}
          <div className="mb-8">
            <div className="flex items-start justify-between mb-6">
              <div>
                <h1 className="text-4xl font-bold text-gray-900 mb-2">Providers Directory</h1>
                <p className="text-gray-600">Manage infrastructure partners and compliance verification.</p>
              </div>
              <Button 
                onClick={() => navigate('/admin/provider-add')}
                className="bg-teal-600 hover:bg-teal-700 text-white font-bold py-2 px-6 flex items-center gap-2"
              >
                <Plus className="w-5 h-5" />
                Onboard New Provider
              </Button>
            </div>

            {/* Metrics */}
            <div className="grid grid-cols-5 gap-6">
              {/* Total Providers */}
              <div className="bg-white rounded-xl border-l-4 border-teal-600 p-6 shadow-sm">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Total Providers</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">{providers.length.toLocaleString()}</p>
                <p className="text-xs text-green-600 font-bold flex items-center gap-1">
                  <TrendingUp className="w-3 h-3" />
                  Active
                </p>
              </div>

              {/* Active */}
              <div className="bg-white rounded-xl border-l-4 border-green-600 p-6 shadow-sm">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Active</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">1,180</p>
                <p className="text-xs text-gray-600">95% Utilization</p>
              </div>

              {/* Pending Verification */}
              <div className="bg-white rounded-xl border-l-4 border-orange-500 p-6 shadow-sm">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Pending Verification</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">12</p>
                <p className="text-xs text-orange-600 font-bold">Requires Review</p>
              </div>

              {/* Compliance Issues */}
              <div className="bg-white rounded-xl border-l-4 border-red-600 p-6 shadow-sm">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Compliance Issues</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">48</p>
                <p className="text-xs text-red-600 font-bold">Action Required</p>
              </div>

              {/* Suspended */}
              <div className="bg-white rounded-xl border-l-4 border-gray-400 p-6 shadow-sm">
                <p className="text-xs font-bold text-gray-600 uppercase tracking-widest mb-2">Suspended</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">2</p>
                <p className="text-xs text-gray-600">Policy Breaches</p>
              </div>
            </div>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-gray-200 p-6 mb-8 shadow-sm">
            <div className="flex items-center gap-4 flex-wrap">
              <div className="flex-1 min-w-xs">
                <div className="relative">
                  <Search className="absolute left-3 top-3 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    placeholder="Filter by name, region, or service..."
                    className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600"
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                  />
                </div>
              </div>
              <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 font-bold text-sm">
                <option>Performance Band: All</option>
                <option>High</option>
                <option>Medium</option>
                <option>Low</option>
              </select>
              <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 font-bold text-sm">
                <option>Compliance Status: All</option>
                <option>Valid</option>
                <option>Expiring</option>
                <option>Expired</option>
              </select>
              <select className="px-4 py-2 border border-gray-300 rounded-lg focus:outline-none focus:border-teal-600 font-bold text-sm">
                <option>Payout Status: All</option>
                <option>Clear</option>
                <option>Held</option>
                <option>No Balance</option>
              </select>
              <button className="text-gray-600 hover:text-gray-900 font-bold text-sm uppercase tracking-widest">
                CLEAR
              </button>
            </div>
          </div>

          {/* Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-gray-200 bg-gray-50">
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Provider</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Service Type</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Coverage</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Compliance</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Jobs</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Completion</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Payout</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Status</th>
                    <th className="px-6 py-4 text-left text-xs font-bold text-gray-700 uppercase tracking-widest">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {displayedProviders.map((provider, idx) => (
                    <tr key={provider.id} className="border-b border-gray-200 hover:bg-gray-50 transition-colors">
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-3">
                          <div className={`w-10 h-10 ${getAvatarColor(idx)} rounded-lg flex items-center justify-center text-white font-bold text-sm shadow-sm`}>
                            {provider.avatar}
                          </div>
                          <div>
                            <p className="font-bold text-gray-900 text-sm">{provider.name}</p>
                            <p className="text-xs text-gray-600">{provider.id}</p>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-bold text-gray-900 text-xs mb-1 uppercase tracking-wide">{provider.serviceType}</p>
                      </td>
                      <td className="px-6 py-4">
                        <p className="text-xs text-gray-600">{provider.coverage}</p>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded text-xs font-bold inline-block ${getComplianceColor(provider.complianceStatus)}`}>
                          {provider.compliance}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <p className="font-bold text-gray-900">{provider.jobs}</p>
                      </td>
                      <td className="px-6 py-4">
                        <div>
                          <p className="font-bold text-gray-900">{provider.completion}%</p>
                          <p className="text-xs text-gray-600">{provider.completionVar}</p>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded text-xs font-bold inline-block ${getPayoutColor(provider.payoutStatus)}`}>
                          {provider.payout}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <span className={`px-3 py-1 rounded text-xs font-bold inline-block ${getStatusBadgeColor(provider.status)}`}>
                          {provider.status}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => navigate(`/admin/provider-performance?id=${provider.id}&name=${encodeURIComponent(provider.name)}`)}
                            className="text-teal-600 hover:text-teal-900 font-bold text-sm transition-colors"
                          >
                            View
                          </button>
                          <button
                            onClick={() => {
                              setSelectedProvider(provider);
                              setFormData({
                                name: provider.name,
                                email: provider.email,
                                phone: provider.phone,
                                status: provider.status === 'SUSPENDED' ? 'suspended' : 'active',
                              });
                              setShowEditModal(true);
                            }}
                            className="text-gray-600 hover:text-gray-900 font-bold text-sm transition-colors"
                          >
                            Edit
                          </button>
                          {provider.status !== 'SUSPENDED' && (
                            <button
                              onClick={() => {
                                setSelectedProvider(provider);
                                setShowSuspendModal(true);
                              }}
                              className="text-red-600 hover:text-red-900 font-bold text-sm transition-colors"
                            >
                              Suspend
                            </button>
                          )}
                          <button
                            onClick={() => {
                              setSelectedProvider(provider);
                              setShowRestrictModal(true);
                            }}
                            className="text-amber-600 hover:text-amber-900 font-bold text-sm transition-colors"
                          >
                            Restrict
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            <div className="px-6 py-4 border-t border-gray-200 flex items-center justify-between">
              <span className="text-sm text-gray-600">
                Showing {startIdx + 1}-{Math.min(startIdx + itemsPerPage, providers.length)} of {providers.length} providers
              </span>
              <div className="flex items-center gap-2">
                <button 
                  onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                  className="px-3 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  ‹
                </button>
                {[1, 2, 3].map((page) => (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`px-3 py-2 rounded-lg font-bold text-sm transition-colors ${
                      currentPage === page
                        ? 'bg-teal-600 text-white'
                        : 'text-gray-600 hover:bg-gray-100'
                    }`}
                  >
                    {page}
                  </button>
                ))}
                <button className="px-3 py-2 text-gray-600 hover:text-gray-900 font-bold text-sm">…</button>
                <button className="px-3 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors">
                  50
                </button>
                <button 
                  onClick={() => setCurrentPage(currentPage + 1)}
                  className="px-3 py-2 text-gray-600 hover:text-gray-900 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
                >
                  ›
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Modals */}
        {showAddModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">Add New Provider</h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleAddProvider();
                }}
              >
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowAddModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                  >
                    Add Provider
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showEditModal && selectedProvider && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">Edit Provider</h2>
              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  handleUpdateProvider();
                }}
              >
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Name</label>
                  <input
                    type="text"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Email</label>
                  <input
                    type="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Phone</label>
                  <input
                    type="tel"
                    value={formData.phone}
                    onChange={(e) => setFormData({ ...formData, phone: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  />
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-2">Status</label>
                  <select
                    value={formData.status}
                    onChange={(e) => setFormData({ ...formData, status: e.target.value })}
                    className="w-full border border-gray-300 rounded-lg px-3 py-2"
                  >
                    <option value="active">Active</option>
                    <option value="inactive">Inactive</option>
                    <option value="suspended">Suspended</option>
                  </select>
                </div>
                <div className="flex justify-end gap-2">
                  <button
                    type="button"
                    onClick={() => setShowEditModal(false)}
                    className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    className="px-4 py-2 bg-teal-600 text-white rounded-lg hover:bg-teal-700"
                  >
                    Update Provider
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}

        {showDeleteModal && selectedProvider && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-lg p-6 w-full max-w-md">
              <h2 className="text-xl font-bold mb-4">Delete Provider</h2>
              <p className="text-gray-600 mb-6">
                Are you sure you want to delete {selectedProvider.name}? This action cannot be undone.
              </p>
              <div className="flex justify-end gap-2">
                <button
                  onClick={() => setShowDeleteModal(false)}
                  className="px-4 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDeleteProvider}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700"
                >
                  Delete
                </button>
              </div>
            </div>
          </div>
        )}
      </AdminLayout>
    </>
  );
};

export default AdminProvidersPage;
